/**
 * Calendar grid maths for the month / week views.
 *
 * Everything here works in the user's own calendar, not in UTC or in the
 * browser's locale: a "month" for a Solar Hijri user has a different shape
 * (29-31 days, leap in year 1403) than a Gregorian one, and the grid has to
 * line up with the engine in `@calendar/shared`.
 */

import {
  addDays,
  civilToInstant,
  daysInMonth,
  instantToCivil,
  jdnWeekday,
  startOfMonth,
  toJdn,
  weekdayName,
  type CalendarEvent,
  type CalendarSystem,
  type CivilDate,
} from "@calendar/shared";

/**
 * Week starts on Saturday.
 *
 * That is the norm in Iran and the Gulf, and a calendar aimed at Persian users
 * should open on the same day their paper diary does. Users who want otherwise
 * can change it later without touching this module.
 */
export const WEEK_START = 6; // 0 = Sunday

/** All the cells of a month grid, padded to whole weeks. */
export function monthGrid(anchor: CivilDate, calendar: CalendarSystem): CivilDate[] {
  const first = startOfMonth(anchor, calendar);
  const lead = (jdnWeekday(toJdn(first, calendar)) - WEEK_START + 7) % 7;
  const start = addDays(first, -lead, calendar);

  const length = daysInMonth(anchor, calendar);
  const total = Math.ceil((lead + length) / 7) * 7;
  return Array.from({ length: total }, (_, i) => addDays(start, i, calendar));
}

/** Seven cells starting on the Saturday of `anchor`'s week. */
export function weekGrid(anchor: CivilDate, calendar: CalendarSystem): CivilDate[] {
  const shift = (jdnWeekday(toJdn(anchor, calendar)) - WEEK_START + 7) % 7;
  const start = addDays(anchor, -shift, calendar);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i, calendar));
}

export function sameDate(a: CivilDate, b: CivilDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/** Weekday labels for a header row, in the same order as the grids. */
export function weekdayLabels(
  calendar: CalendarSystem,
  lang: "fa" | "en",
  first: CivilDate,
): string[] {
  return weekGrid(first, calendar).map((d) => weekdayName(d, calendar, lang));
}

/** True when `iso` falls on the same local day as `date` in `timeZone`. */
export function isOnDay(iso: string, date: CivilDate, timeZone: string, calendar: CalendarSystem): boolean {
  const local = instantToCivil(iso, timeZone, calendar).date;
  return sameDate(local, date);
}

export interface PositionedEvent {
  event: CalendarEvent;
  /** Minutes since local midnight, used to place the block in a day column. */
  startMinutes: number;
  endMinutes: number;
  allDay: boolean;
}

/**
 * Bucket events into a day grid.
 *
 * A timed event is laid out by its local wall-clock time, and an all-day event
 * goes to `allDay` on every day it spans, which is what a real calendar does.
 */
export function layoutEvents(
  events: CalendarEvent[],
  days: CivilDate[],
  timeZone: string,
  calendar: CalendarSystem,
): Map<string, PositionedEvent[]> {
  const byDay = new Map<string, PositionedEvent[]>();
  for (const day of days) byDay.set(dateKey(day), []);

  const dayIndex = new Map(days.map((d, i) => [dateKey(d), i]));

  for (const event of events) {
    if (event.allDay) {
      for (const day of days) {
        if (isOnDay(event.startUtc, day, timeZone, calendar)) {
          byDay.get(dateKey(day))!.push({ event, startMinutes: 0, endMinutes: 0, allDay: true });
        }
      }
      continue;
    }

    const start = instantToCivil(event.startUtc, timeZone, calendar);
    const end = instantToCivil(event.endUtc, timeZone, calendar);
    const startDay = dateKey(start.date);

    // Place the block on the day it starts, and draw a tail on later days so a
    // meeting that runs past midnight does not vanish from the grid.
    const bucket = byDay.get(startDay);
    if (bucket) {
      bucket.push({
        event,
        startMinutes: start.hour * 60 + start.minute,
        endMinutes: sameDate(end.date, start.date)
          ? end.hour * 60 + end.minute
          : 24 * 60,
        allDay: false,
      });
    } else if (dayIndex.has(startDay)) {
      byDay.get(startDay)!.push({
        event,
        startMinutes: start.hour * 60 + start.minute,
        endMinutes: 24 * 60,
        allDay: false,
      });
    }
  }

  for (const list of byDay.values()) {
    list.sort((a, b) =>
      a.allDay === b.allDay
        ? a.startMinutes - b.startMinutes || a.event.title.localeCompare(b.event.title)
        : a.allDay ? -1 : 1,
    );
  }
  return byDay;
}

export function dateKey(d: CivilDate): string {
  return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
}

/** The `[from, to)` instant range covering whole local days. */
export function rangeForDays(
  days: CivilDate[],
  timeZone: string,
  calendar: CalendarSystem,
): { from: string; to: string } {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  return {
    from: civilToInstant(first, 0, 0, timeZone, calendar),
    to: civilToInstant(addDays(last, 1, calendar), 0, 0, timeZone, calendar),
  };
}
